import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Clock, MessageSquare, Plus, Minus } from 'lucide-react';
import { useWebSocketStore } from '../stores/websocketStore';

interface ChatExchange {
  request: {
    id: string;
    timestamp: string;
    user_message: string;
    context_summary: any;
    full_object: any;
  };
  response?: {
    id: string;
    timestamp: string;
    response: string;
    tokens_used: number;
    cost: number;
    cache_used: boolean;
    context_summary: any;
    full_object: any;
    model: string;
  };
  duration?: number;
}

export function ChatDebugConsole() {
  const [chatExchanges, setChatExchanges] = useState<ChatExchange[]>([]);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [expandedResponses, setExpandedResponses] = useState<Set<string>>(new Set());
  const { status } = useWebSocketStore();
  const [filter] = useState<'all' | 'requests' | 'responses'>('all');

  useEffect(() => {
    const handleChatEvent = (event: CustomEvent) => {
      if (!event.detail) {
        console.warn('ChatDebugConsole: Received event with no detail');
        return;
      }
      
      const { type, payload, request_id } = event.detail;
      
      if (!payload) {
        console.warn('ChatDebugConsole: Received event with no payload');
        return;
      }
      
      if (type === 'chat_request') {
        setChatExchanges(prev => [...prev, {
          request: {
            id: payload.family_id || Date.now().toString(),
            timestamp: new Date().toISOString(),
            user_message: payload.message || 'No message',
            context_summary: payload.context_summary || {},
            full_object: payload
          },
          duration: payload.duration
        }]);
      } else if (type === 'chat_response') {
        setChatExchanges(prev => prev.map(exchange => {
          const requestId = request_id || payload.family_id;
          if (!exchange.response && (exchange.request.id === requestId || exchange.request.user_message === requestId)) {
            return {
              ...exchange,
              response: {
                id: payload.family_id || Date.now().toString(),
                timestamp: new Date().toISOString(),
                response: payload.response || 'No response',
                tokens_used: payload.tokens_used || 0,
                cost: payload.cost || 0,
                cache_used: payload.cache_used || false,
                context_summary: payload.context_summary || {},
                full_object: payload,
                model: payload.model || 'gpt-3.5-turbo'
              }
            };
          }
          return exchange;
        }));
      }
    };

    window.addEventListener('chatDebug', handleChatEvent as EventListener);

    return () => {
      window.removeEventListener('chatDebug', handleChatEvent as EventListener);
    };
  }, []);

  const toggleRequestExpanded = (id: string) => {
    setExpandedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleResponseExpanded = (id: string) => {
    setExpandedResponses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allRequestIds = new Set(chatExchanges.map(e => e.request.id));
    const allResponseIds = new Set(chatExchanges.filter(e => e.response).map(e => e.response!.id));
    setExpandedRequests(allRequestIds);
    setExpandedResponses(allResponseIds);
  };

  const collapseAll = () => {
    setExpandedRequests(new Set());
    setExpandedResponses(new Set());
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const filteredExchanges = chatExchanges.filter(exchange => {
    if (filter === 'requests') return true;
    if (filter === 'responses') return !!exchange.response;
    return true;
  });

  return (
    <div className="h-full flex flex-col bg-background border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/40">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          <h3 className="font-semibold text-sm">Chat Debug Console</h3>
          <div className={`w-2 h-2 rounded-full ${status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-xs text-muted-foreground">
            {status === 'connected' ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            <Plus className="h-3 w-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            <Minus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {filteredExchanges.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mb-2" />
            <p className="text-xs">No chat data available</p>
            <p className="text-xs">Start a conversation to see debug data</p>
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {filteredExchanges.map((exchange, index) => (
              <div key={exchange.request.id} className="border rounded-md bg-card">
                <div className="p-3 flex gap-3">
                  {/* Timestamp on the left */}
                  <div className="flex flex-col items-center justify-start pt-1">
                    <Clock className="h-3 w-3 text-muted-foreground mb-1" />
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(exchange.request.timestamp)}
                    </span>
                    {exchange.duration && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded font-mono mt-1">
                        {exchange.duration}ms
                      </span>
                    )}
                  </div>

                  {/* Request and Response on the right */}
                  <div className="flex-1 min-w-0">
                    {/* Request Section */}
                    <div className="mb-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRequestExpanded(exchange.request.id)}
                          className="h-4 w-4 p-0"
                        >
                          {expandedRequests.has(exchange.request.id) ? (
                            <Minus className="h-3 w-3" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                        </Button>
                        <span className="text-blue-400 font-semibold text-sm">User: "{exchange.request.user_message}"</span>
                      </div>
                      
                      {expandedRequests.has(exchange.request.id) && (
                        <div className="ml-6 mt-2 space-y-2">
                          <div className="bg-muted/30 rounded p-2">
                            <div className="text-xs font-semibold text-muted-foreground mb-1">Request Details:</div>
                            <div className="text-xs space-y-1">
                              <div><span className="text-muted-foreground">ID:</span> {exchange.request.id}</div>
                              <div><span className="text-muted-foreground">Timestamp:</span> {exchange.request.timestamp}</div>
                              <div><span className="text-muted-foreground">Message:</span> {exchange.request.user_message}</div>
                            </div>
                          </div>
                          
                          <div className="bg-muted/30 rounded p-2">
                            <div className="text-xs font-semibold text-muted-foreground mb-1">Context Summary:</div>
                            <div className="text-xs max-h-32 overflow-y-auto">
                              <pre className="whitespace-pre-wrap text-muted-foreground">
                                {JSON.stringify(exchange.request.context_summary || null, null, 2)}
                              </pre>
                            </div>
                          </div>
                          
                          <div className="bg-muted/30 rounded p-2">
                            <div className="text-xs font-semibold text-muted-foreground mb-1">Full Request Object:</div>
                            <div className="text-xs max-h-48 overflow-y-auto">
                              <pre className="whitespace-pre-wrap text-muted-foreground">
                                {JSON.stringify(exchange.request.full_object, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Response Section */}
                    {exchange.response ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleResponseExpanded(exchange.response!.id)}
                            className="h-4 w-4 p-0"
                          >
                            {expandedResponses.has(exchange.response!.id) ? (
                              <Minus className="h-3 w-3" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                          </Button>
                          <span className="text-sm text-green-300">
                            {exchange.response.model}: {exchange.response.response.substring(0, 200)}
                            {exchange.response.response.length > 200 ? '...' : ''}
                          </span>
                        </div>
                        
                        {expandedResponses.has(exchange.response!.id) && (
                          <div className="ml-6 mt-2 space-y-2">
                            <div className="bg-muted/30 rounded p-2">
                              <div className="text-xs font-semibold text-muted-foreground mb-1">Response Details:</div>
                              <div className="text-xs space-y-1">
                                <div><span className="text-muted-foreground">ID:</span> {exchange.response!.id}</div>
                                <div><span className="text-muted-foreground">Timestamp:</span> {exchange.response!.timestamp}</div>
                                <div><span className="text-muted-foreground">Model:</span> {exchange.response!.model}</div>
                                <div><span className="text-muted-foreground">Tokens Used:</span> {exchange.response!.tokens_used}</div>
                                <div><span className="text-muted-foreground">Cost:</span> ${exchange.response!.cost.toFixed(6)}</div>
                                <div><span className="text-muted-foreground">Cache:</span> {exchange.response!.cache_used ? 'HIT' : 'MISS'}</div>
                              </div>
                            </div>
                            
                            <div className="bg-muted/30 rounded p-2">
                              <div className="text-xs font-semibold text-muted-foreground mb-1">Context Summary:</div>
                              <div className="text-xs max-h-32 overflow-y-auto">
                                <pre className="whitespace-pre-wrap text-muted-foreground">
                                  {JSON.stringify(exchange.response!.context_summary || null, null, 2)}
                                </pre>
                              </div>
                            </div>
                            
                            <div className="bg-muted/30 rounded p-2">
                              <div className="text-xs font-semibold text-muted-foreground mb-1">Full Response Object:</div>
                              <div className="text-xs max-h-48 overflow-y-auto">
                                <pre className="whitespace-pre-wrap text-muted-foreground">
                                  {JSON.stringify(exchange.response!.full_object, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-yellow-400 text-sm ml-6">
                        [WAITING] Response pending...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
